import { OmButton, omNotification } from '@owma/kit'
import i18n from 'i18n'
import settingSyncService from 'services/settingSync'
import { isPopupMode } from 'services/popup/isPopupMode'

// OMD-225: settings 서버 로드/저장 실패를 사용자에게 고지하는 배너.
// - load 실패(serverLoadState==='failed'): 저장이 차단되므로 재로딩을 안내. loaded 회복 시 자동 닫힘.
// - save 실패(재시도 소진): 재시도 버튼 제공. (Phase 3-b 에서 saga 가 호출)
const LOAD_FAILED_KEY = 'omd225-settings-load-failed'
const SAVE_FAILED_KEY = 'omd225-settings-save-failed'
const SCHEMA_ERROR_KEY = 'omd225-settings-schema-error'

let registered = false

/**
 * 앱 시작 시 1회 등록한다(popup 제외).
 * serverLoadState 변화를 구독해 load 실패/회복에 따라 배너를 띄우거나 닫는다.
 */
export const registerSettingsSyncNotice = (): void => {
  if (registered || isPopupMode()) return
  registered = true

  settingSyncService.addServerLoadStateListener((state) => {
    if (state === 'failed') {
      omNotification.error({
        key: LOAD_FAILED_KEY,
        message: i18n.t('settings.syncGuard.loadFailed.title'),
        description: (
          <>
            {i18n.t('settings.syncGuard.loadFailed.body')}
            <div style={{ marginTop: 8 }}>
              <OmButton variant="primary" size="sm" onClick={() => window.location.reload()}>
                {i18n.t('settings.syncGuard.reload')}
              </OmButton>
            </div>
          </>
        ),
        duration: 0,
      })
    } else if (state === 'loaded') {
      omNotification.destroy(LOAD_FAILED_KEY)
    }
  })
}

/**
 * 저장(save/PUT) 재시도 소진 실패 시 호출한다. 재시도 액션(onRetry)을 배너 버튼으로 제공한다.
 */
export const notifySettingsSaveFailed = (onRetry: () => void): void => {
  if (isPopupMode()) return
  omNotification.error({
    key: SAVE_FAILED_KEY,
    message: i18n.t('settings.syncGuard.saveFailed.title'),
    description: (
      <>
        {i18n.t('settings.syncGuard.saveFailed.body')}
        <div style={{ marginTop: 8 }}>
          <OmButton
            variant="primary"
            size="sm"
            onClick={() => {
              omNotification.destroy(SAVE_FAILED_KEY)
              onRetry()
            }}
          >
            {i18n.t('settings.syncGuard.retry')}
          </OmButton>
        </div>
      </>
    ),
    duration: 0,
  })
}

/** 저장이 성공하면 저장 실패 배너를 닫는다. */
export const dismissSettingsSaveFailed = (): void => {
  omNotification.destroy(SAVE_FAILED_KEY)
}

/**
 * 설정 스키마 검증 실패(데이터 malformed) 시 호출한다. 재시도가 무의미하므로 재로딩을 유도한다.
 * (기존 settingSync 의 crude window.alert 를 대체·통일)
 */
export const notifySettingsSchemaError = (): void => {
  if (isPopupMode()) return
  omNotification.error({
    key: SCHEMA_ERROR_KEY,
    message: i18n.t('settings.syncGuard.schemaError.title'),
    description: (
      <>
        {i18n.t('settings.syncGuard.schemaError.body')}
        <div style={{ marginTop: 8 }}>
          <OmButton variant="primary" size="sm" onClick={() => window.location.reload()}>
            {i18n.t('settings.syncGuard.reload')}
          </OmButton>
        </div>
      </>
    ),
    duration: 0,
  })
}
